// Describes where an overdue invoice stands in the charging interest process:
// one step per circle in the UI, plus the single next step the user should take.
//
// Unlike the payment reminder process, the dates and the legality of the next
// step are worked out on the server, in InvoiceInterestChargeTerms. The rate
// history the answer depends on lives there, and the amount shown here has to
// be the same one the generated invoice will carry.

import Decimal from '@/utils/decimal';

export type InterestStepKey = 'invoiceSent' | 'interestInvoice' | 'paid';

// done       - the step has been carried out
// actionable - the step should be carried out now
// waiting    - the step cannot lawfully be carried out yet
// idle       - there is nothing to do for this step
export type InterestStepState = 'done' | 'actionable' | 'waiting' | 'idle';

export type InterestActionType = 'createInterestInvoice' | 'wait' | 'none';

// Amounts arrive as Decimal objects through the scalar link, but the process
// only ever hands them on to be formatted, so it does not care which it got.
export type InterestAmount = Decimal | string;

// Why an interest invoice may not be written. Mirrors the reasons the server
// returns, and decides which explanation the circle shows.
export type InterestBlockedReason =
  | 'feature_disabled'
  | 'already_charged'
  | 'paid'
  | 'too_early'
  | 'nothing_to_charge';

export interface IProcessInterestPreview {
  chargeable: boolean;
  blockedReason: InterestBlockedReason | string | null;
  availableFrom: string;
  totalAmount: InterestAmount;
}

export interface IProcessInterestCharge {
  id: string;
  interestInvoice: {
    id: string;
    number: number | null;
  };
}

export interface IInterestProcessInvoice {
  paidDate?: string | null;
  invoiceInterestCharges: IProcessInterestCharge[];
  // Null when the unit does not charge interest, so the whole process is idle.
  interestChargePreview?: IProcessInterestPreview | null;
}

export interface IInterestStep {
  key: InterestStepKey;
  state: InterestStepState;
  // The first date an interest invoice may be written, when we can know it.
  availableFrom: string | null;
  blockedReason: string | null;
}

export interface IInterestAction {
  type: InterestActionType;
  availableFrom: string | null;
  // The amount the interest invoice would be written for, so the button can
  // say what it is about to do.
  totalAmount: InterestAmount | null;
}

export interface IInterestProcess {
  steps: IInterestStep[];
  action: IInterestAction;
}

export const existingCharge = (
  invoice: IInterestProcessInvoice,
): IProcessInterestCharge | undefined => {
  return invoice.invoiceInterestCharges[0];
};

// A blocked reason only stops the flow while there is still something to wait
// for. Once interest has been charged the step is done, not waiting, and a
// paid invoice ends the process rather than blocking it.
const interestStepState = (invoice: IInterestProcessInvoice): InterestStepState => {
  const preview = invoice.interestChargePreview;

  if (existingCharge(invoice)) return 'done';
  if (!preview) return 'idle';
  if (preview.chargeable) return 'actionable';
  if (invoice.paidDate) return 'idle';

  return 'waiting';
};

const buildInterestStep = (invoice: IInterestProcessInvoice): IInterestStep => {
  const preview = invoice.interestChargePreview;

  return {
    key: 'interestInvoice',
    state: interestStepState(invoice),
    availableFrom: preview ? preview.availableFrom : null,
    blockedReason: preview ? preview.blockedReason : null,
  };
};

const nextAction = (
  invoice: IInterestProcessInvoice,
  interestStep: IInterestStep,
): IInterestAction => {
  const preview = invoice.interestChargePreview;
  const nothing: IInterestAction = { type: 'none', availableFrom: null, totalAmount: null };

  if (!preview || interestStep.state === 'done' || interestStep.state === 'idle') {
    return nothing;
  }

  if (interestStep.state === 'actionable') {
    return {
      type: 'createInterestInvoice',
      availableFrom: null,
      totalAmount: preview.totalAmount,
    };
  }

  return { type: 'wait', availableFrom: preview.availableFrom, totalAmount: null };
};

export const buildInterestChargeProcess = (
  invoice: IInterestProcessInvoice,
): IInterestProcess => {
  const interestStep = buildInterestStep(invoice);
  const paid = !!invoice.paidDate;

  let paidState: InterestStepState = 'idle';
  if (paid) paidState = 'done';
  else if (interestStep.state === 'done') paidState = 'actionable';

  const steps: IInterestStep[] = [
    { key: 'invoiceSent', state: 'done', availableFrom: null, blockedReason: null },
    interestStep,
    { key: 'paid', state: paidState, availableFrom: null, blockedReason: null },
  ];

  return { steps, action: nextAction(invoice, interestStep) };
};
